import { Card, CardContent } from "@/components/ui/card";
import { Heart, Activity, TrendingUp, Thermometer, AlertCircle, Video } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useState, useEffect } from "@/utils/react-singleton";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

// Types for the last triage data
export interface LastTriageDataProps {
  className?: string;
  variant?: "sidebar" | "card";
  showTriageButton?: boolean;
}

export interface TriageSubmission {
  id: string;
  created_at: string;
  urgency: string;
  vitals: {
    temperature?: string;
    heart_rate?: string;
    blood_pressure?: string;
    respiratory_rate?: string;
    oxygen_saturation?: string;
    pain_score?: string;
  };
  symptoms: string[];
  primary_symptoms?: string[];
  recommendation?: string;
  clinical_notes?: string;
}

export function LastTriageData({ 
  className, 
  variant = "card",
  showTriageButton = false
}: LastTriageDataProps) {
  const [lastTriage, setLastTriage] = useState<TriageSubmission | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user, profile } = useAuth();

  // Helper function to get urgency color
  const getUrgencyColor = (urgency: string) => {
    switch (urgency?.toLowerCase()) {
      case 'immediate': return "text-red-600";
      case 'very urgent': return "text-orange-600";
      case 'urgent': return "text-yellow-600";
      case 'standard': return "text-green-600";
      case 'non-urgent': return "text-blue-600";
      default: return "text-gray-600";
    }
  };

  // Format date to readable string
  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Fetch last triage data
  useEffect(() => {
    const fetchLastTriageData = async () => {
      if (!profile?.id) return;
      
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('triage_submissions')
          .select('*')
          .eq('patient_id', profile.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
        
        if (error) {
          console.error('Error fetching last triage data:', error);
          setLastTriage(null);
        } else {
          console.log('Last triage data fetched:', data);
          setLastTriage(data);
        }
      } catch (error) {
        console.error('Exception fetching last triage data:', error);
        setLastTriage(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLastTriageData();
  }, [profile?.id]);

  // Sidebar variant
  if (variant === "sidebar") {
    // Loading state
    if (isLoading) {
      return (
        <div className={cn("space-y-3 p-3", className)}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Last Triage</span>
          </div>
          <div className="animate-pulse space-y-2">
            <div className="h-3 bg-gray-200 rounded w-3/4"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            <div className="h-3 bg-gray-200 rounded w-2/3"></div>
          </div>
          {showTriageButton && (
            <div className="space-y-2 mt-2">
              <Button size="sm" className="w-full" asChild>
                <Link to="/patient/triage">Start New Triage</Link>
              </Button>
              <Button size="sm" variant="outline" className="w-full" asChild>
                <Link to="/patient/tele-triage">
                  <Video className="w-3 h-3 mr-1" />
                  Tele Doctor
                </Link>
              </Button>
            </div>
          )}
        </div>
      );
    }

    // No triage data found
    if (!lastTriage) {
      return (
        <div className={cn("space-y-3 p-3", className)}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Last Triage</span>
          </div>
          <div className="flex flex-col items-center justify-center py-3 text-center">
            <AlertCircle className="h-6 w-6 text-gray-400 mb-2" />
            <p className="text-xs text-muted-foreground">No previous triage</p>
          </div>
          {showTriageButton && (
            <div className="space-y-2 mt-2">
              <Button size="sm" className="w-full" asChild>
                <Link to="/patient/triage">Start New Triage</Link>
              </Button>
              <Button size="sm" variant="outline" className="w-full" asChild>
                <Link to="/patient/tele-triage">
                  <Video className="w-3 h-3 mr-1" />
                  Tele Doctor
                </Link>
              </Button>
            </div>
          )}
        </div>
      );
    }

    // With triage data
    return (
      <div className={cn("space-y-3 p-3", className)}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Last Triage</span>
          <span className="text-xs text-muted-foreground">
            {formatDate(lastTriage.created_at)}
          </span>
        </div>
        
        <div className="space-y-2">
          {/* Vital Signs */}
          {lastTriage.vitals?.heart_rate && (
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Heart className="h-3 w-3 text-red-500" /> Heart Rate
              </span>
              <span className="text-sm font-medium">{lastTriage.vitals.heart_rate}</span>
            </div>
          )}
          
          {lastTriage.vitals?.blood_pressure && (
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <TrendingUp className="h-3 w-3 text-blue-500" /> Blood Pressure
              </span>
              <span className="text-sm font-medium">{lastTriage.vitals.blood_pressure}</span>
            </div>
          )}
          
          {lastTriage.vitals?.temperature && (
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Thermometer className="h-3 w-3 text-orange-500" /> Temperature
              </span>
              <span className="text-sm font-medium">
                {(() => {
                  const temp = lastTriage.vitals.temperature;
                  const numTemp = typeof temp === 'number' ? temp : parseFloat(String(temp));
                  return isNaN(numTemp) ? String(temp) : `${numTemp.toFixed(1)}°C`;
                })()}
              </span>
            </div>
          )}
          
          {lastTriage.vitals?.oxygen_saturation && (
            <div className="flex justify-between items-center">
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Activity className="h-3 w-3 text-green-500" /> O₂ Saturation
              </span>
              <span className="text-sm font-medium">{lastTriage.vitals.oxygen_saturation}</span>
            </div>
          )}
          
          {/* Urgency Level */}
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Activity className="h-3 w-3 text-purple-500" /> Urgency
            </span>
            <span className={cn("text-sm font-medium", getUrgencyColor(lastTriage.urgency))}>
              {lastTriage.urgency?.charAt(0).toUpperCase() + lastTriage.urgency?.slice(1) || "Unknown"}
            </span>
          </div>
          
          {/* Primary Symptoms */}
          {lastTriage.symptoms && lastTriage.symptoms.length > 0 && (
            <div className="pt-1 mt-1 border-t">
              <span className="text-xs text-muted-foreground block mb-1">Primary Symptoms:</span>
              <div className="flex flex-wrap gap-1">
                {(lastTriage.primary_symptoms || lastTriage.symptoms.slice(0, 2)).map((symptom, index) => (
                  <span key={index} className="text-xs bg-gray-100 px-1.5 py-0.5 rounded-full">
                    {symptom}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Recommendation if available */}
          {lastTriage.recommendation && (
            <div className="pt-1 mt-1 border-t">
              <span className="text-xs text-muted-foreground block mb-1">Recommendation:</span>
              <p className="text-xs">{lastTriage.recommendation}</p>
            </div>
          )}
        </div>

        {showTriageButton && (
          <div className="space-y-2 mt-2">
            <Button size="sm" className="w-full" asChild>
              <Link to="/patient/triage">Start New Triage</Link>
            </Button>
            <Button size="sm" variant="outline" className="w-full" asChild>
              <Link to="/patient/tele-triage">
                <Video className="w-3 h-3 mr-1" />
                Tele Doctor
              </Link>
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Default card variant
  // Loading state
  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="pt-6 flex items-center justify-center h-40">
          <div className="animate-pulse flex flex-col items-center">
            <div className="h-4 w-32 bg-gray-200 rounded mb-4"></div>
            <div className="h-3 w-24 bg-gray-200 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // No triage data found
  if (!lastTriage) {
    return (
      <Card className={className}>
        <CardContent className="pt-6 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Last Triage</span>
          </div>
          <div className="flex flex-col items-center justify-center h-32 text-center">
            <Activity className="h-8 w-8 text-gray-400 mb-2" />
            <p className="text-sm text-muted-foreground">No triage data available</p>
            <p className="text-xs text-muted-foreground mt-1">Complete a health assessment to see your data</p>
          </div>
          {showTriageButton && (
            <div className="space-y-2 mt-2">
              <Button className="w-full" asChild>
                <Link to="/patient/triage">Start New Triage</Link>
              </Button>
              <Button variant="outline" className="w-full" asChild>
                <Link to="/patient/tele-triage">
                  <Video className="w-4 h-4 mr-2" />
                  Connect with Tele Doctor
                </Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // With triage data
  return (
    <Card className={className}>
      <CardContent className="pt-6 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Last Triage</span>
          <span className="text-xs text-muted-foreground">
            {formatDate(lastTriage.created_at)}
          </span>
        </div>
        
        <div className="space-y-2">
          {lastTriage.vitals?.heart_rate && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Heart className="h-4 w-4 text-red-500" /> Heart Rate
              </span>
              <span className="font-medium">{lastTriage.vitals.heart_rate}</span>
            </div>
          )}
          
          {lastTriage.vitals?.blood_pressure && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <TrendingUp className="h-4 w-4 text-blue-500" /> Blood Pressure
              </span>
              <span className="font-medium">{lastTriage.vitals.blood_pressure}</span>
            </div>
          )}
          
          {lastTriage.vitals?.temperature && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Thermometer className="h-4 w-4 text-orange-500" /> Temperature
              </span>
              <span className="font-medium">{lastTriage.vitals.temperature}</span>
            </div>
          )}
          
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <Activity className="h-4 w-4 text-purple-500" /> Urgency
            </span>
            <span className={cn("font-medium", getUrgencyColor(lastTriage.urgency))}>
              {lastTriage.urgency?.charAt(0).toUpperCase() + lastTriage.urgency?.slice(1) || "Unknown"}
            </span>
          </div>
          
          {lastTriage.symptoms && lastTriage.symptoms.length > 0 && (
            <div className="pt-2 border-t mt-2">
              <span className="text-sm text-muted-foreground">Primary Symptoms:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {(lastTriage.primary_symptoms || lastTriage.symptoms.slice(0, 3)).map((symptom, index) => (
                  <span key={index} className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                    {symptom}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {showTriageButton && (
          <div className="space-y-2 mt-2">
            <Button className="w-full" asChild>
              <Link to="/patient/triage">Start New Triage</Link>
            </Button>
            <Button variant="outline" className="w-full" asChild>
              <Link to="/patient/tele-triage">
                <Video className="w-4 h-4 mr-2" />
                Connect with Tele Doctor
              </Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
